import { apiFetch } from '@/lib/api/client';
import type { MarketplacePurchaseResultItem } from '@/lib/api/hooks';
import { paidPurchaseLines } from './purchaseRequest';
import type { CartItem } from './stores/cart';

/** One stack bought as its own purchase: both lines on one term, at the stack
 *  price. Promo codes and cart promotions never ride on it; the server prices
 *  each line from the stack table and refuses anything else. */
export interface StackPurchaseIn {
  stack_id: string;
  /** A pay link's slug: buys only the product(s) it lists, at their share. */
  stack_recovery?: string;
  purchase_request_id: string;
  items: { sanity_product_id: string; frequency: NonNullable<CartItem['frequency']> }[];
  payment_method_id?: string;
}

export function purchaseStack(body: StackPurchaseIn) {
  return apiFetch<{ items: MarketplacePurchaseResultItem[] }>(
    '/me/marketplace/purchase',
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/** Refusals that mean the stack can't be bought as it is in the cart. */
const REFUSED_STACK_CODES = new Set([
  'stack_unavailable',
  'stack_mismatch',
  'already_subscribed',
  'stack_recovery_not_found',
  'stack_recovery_closed',
]);

const names = (items: CartItem[]) => items.map((item) => item.title).join(' and ');

/** A refusal meaning the stack can't be bought as it is in the cart (nothing
 *  was charged): the lines to take out of the cart and what to tell the
 *  patient. Owning one of its products only drops that line when the server
 *  names it, and the other goes back to its single price (decision 5);
 *  otherwise the whole stack leaves. Null for any other failure. */
export function stackRefusal(
  cause: unknown,
  lines: CartItem[],
): { removeIds: string[]; message: string } | null {
  const error = cause as {
    code?: string;
    message?: string;
    payload?: { product_id?: unknown };
  } | null;
  if (!REFUSED_STACK_CODES.has(error?.code ?? '')) return null;
  const all = lines.map((line) => line.id);
  const title = lines[0]?.stack_title ?? names(lines);
  // A pay-link line already owned: the stack it finishes is complete.
  if (error?.code === 'already_subscribed' && lines.some((line) => line.stack_recovery))
    return {
      removeIds: all,
      message: `You already have ${names(lines)}, so your stack is complete. It was removed from your cart. Nothing was charged.`,
    };
  if (error?.code === 'already_subscribed') {
    const productId = error.payload?.product_id;
    const owned = typeof productId === 'string'
      ? lines.find((line) => line.stack_product === productId)
      : undefined;
    const rest = lines.filter((line) => line !== owned);
    if (owned && rest.length)
      return {
        removeIds: [owned.id],
        message:
          `You already have ${owned.title}, so the stack price doesn't apply. ` +
          `${names(rest)} ${rest.length === 1 ? 'is' : 'are'} still in your cart at ${rest.length === 1 ? 'its' : 'their'} own price. Nothing was charged.`,
      };
    return {
      removeIds: all,
      message:
        `You already have one of the treatments in the ${title}, so the stack price doesn't apply and it was removed from your cart. ` +
        'Nothing was charged. You can add the other treatment from the Shop at its own price.',
    };
  }
  const reason = error?.message || 'The stack could not be purchased.';
  return { removeIds: all, message: `${reason} It was removed from your cart. Nothing was charged for it.` };
}

const requestKey = (stackId: string) => `system.shop.stack-request.v1:${stackId}`;

/** The same request id across a timeout or reload of the same stack, term,
 *  card and patient, so a retry resumes the purchase instead of starting a
 *  second one. Anything else is a new purchase. */
export function stackPurchaseRequestId(
  stackId: string,
  lines: CartItem[],
  paymentMethod: string | null,
  ownerId: string,
): string {
  const fingerprint = JSON.stringify({
    ownerId,
    paymentMethod,
    recovery: lines[0]?.stack_recovery ?? null,
    lines: lines.map((line) => [line.id, line.frequency ?? 'monthly']),
  });
  try {
    const saved = JSON.parse(sessionStorage.getItem(requestKey(stackId)) ?? 'null');
    if (saved?.fingerprint === fingerprint && typeof saved.id === 'string')
      return saved.id;
  } catch {
    /* unreadable storage never resumes a purchase */
  }
  const id = crypto.randomUUID();
  try {
    sessionStorage.setItem(requestKey(stackId), JSON.stringify({ id, fingerprint }));
  } catch {
    /* optional persistence */
  }
  return id;
}

export function retireStackPurchaseRequest(stackId: string): void {
  try {
    sessionStorage.removeItem(requestKey(stackId));
  } catch {
    /* optional persistence */
  }
}

/** The pay link the server opened for a stack's declined line, from its
 *  partial-failure 402 (`details.stack_recovery`). */
function recoverySlug(cause: unknown): string | null {
  const slug = (cause as { details?: { stack_recovery?: unknown } } | null)?.details
    ?.stack_recovery;
  return typeof slug === 'string' && slug.trim() ? slug : null;
}

/** A stack where one product was paid and the other's payment failed. The
 *  server keeps the paid one and opens a pay link for the other. With the
 *  link's slug in the 402, the unpaid line stays in the cart as a pay-link
 *  line (`retry`) on its term, at the share it was sold at, and the next
 *  confirm buys it with the link and a new request id: the declined attempt
 *  is closed, so its own id can't be reused. Without a slug both lines leave
 *  the cart and the server emails the link. */
export function stackPartialFailure(
  cause: unknown,
  lines: CartItem[],
): {
  paid: CartItem[];
  unpaid: CartItem[];
  retry: Omit<CartItem, 'quantity'>[] | null;
  message: string;
} | null {
  const paidIds = new Set(
    paidPurchaseLines(cause).map((line) => line.sanity_product_id),
  );
  const paid = lines.filter((line) => paidIds.has(line.id));
  const unpaid = lines.filter((line) => !paidIds.has(line.id));
  if (!paid.length || !unpaid.length) return null;
  const slug = recoverySlug(cause);
  const retry = slug
    ? unpaid.map(({ quantity: _quantity, ...line }) => {
        const term = line.frequency ?? 'monthly';
        const share = { [term]: line.price_cents };
        return {
          ...line,
          frequency: term,
          plan_prices: share,
          renewal_prices: share,
          stack_recovery: slug,
        };
      })
    : null;
  const declined = cause instanceof Error && cause.message ? ` ${cause.message}` : '';
  return {
    paid,
    unpaid,
    retry,
    message: retry
      ? `${names(paid)} is purchased. The payment for ${names(unpaid)} didn't go through.${declined} ` +
        `${names(unpaid)} is still in your cart at your stack price: confirm again, or choose another card first. ` +
        `If you don't finish now, we'll email you a link to pay within 7 days.`
      : `${names(paid)} is purchased. The payment for ${names(unpaid)} didn't go through. ` +
        `We'll email you a secure link to pay for it at your stack price. Pay within 7 days to keep the stack price.`,
  };
}
